import React, { useEffect, useState } from "react";
import Notification from "../../components/Notification";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../../config";
import { Link } from "react-router-dom";

function DocumentUpdate() {
    const { id } = useParams();
    const [document, setDocument] = useState({
        name: "",
        description: "",
        document_type_id: "",
        status: "Active",
        file: null
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(!!id);
    const [notification, setNotification] = useState(null);
    const token = localStorage.getItem("access_token");
    const [documentTypes, setDocumentTypes] = useState([]);
    const [existingFileName, setExistingFileName] = useState("");
    const [initialData, setInitialData] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDocument = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/documents/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setInitialData(data); // Store initial data for comparison
                        setDocument({
                            name: data.name || "",
                            description: data.description || "",
                            document_type_id: data.document_type_id ? data.document_type_id.toString() : "",
                            status: data.status || "Active",
                            file: null
                        });
                        setExistingFileName(data.file_name || "");
                        setIsEditing(true);
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "Document not found." });
                    } else if (response.status === 401) {
                        navigate("/logout", { state: { from: location } });
                    } else {
                        throw new Error("Failed to fetch document.");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the document." });
                }
            }
        };

        const fetchDocumentTypes = async () => {
            try {
                const response = await fetch(`${API_URL}/documents/types`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setDocumentTypes(data.data || data);
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "Document types not found." });
                } else if (response.status === 401) {
                    navigate("/logout", { state: { from: location } });
                } else {
                    throw new Error("Failed to fetch document types.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching document types." });
            }
        };

        const fetchData = async () => {
            await fetchDocumentTypes();
            if (id) {
                await fetchDocument();
            }
            setLoading(false);
        };

        fetchData();
    }, [id, navigate, token, location]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDocument({ ...document, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['.pdf', '.doc', '.docx'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            
            if (!allowedTypes.includes(fileExtension)) {
                setErrors({ 
                    ...errors, 
                    file: ["Only PDF and Word documents are allowed."] 
                });
                e.target.value = ''; // Clear the file input
                return;
            }

            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setErrors({ 
                    ...errors, 
                    file: ["File size must be less than 10MB."] 
                });
                e.target.value = ''; // Clear the file input
                return;
            }

            setDocument({ ...document, file });
            setErrors({ ...errors, file: null });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrors({});

        // Validate required fields
        if (!document.name.trim()) {
            setErrors({ name: ["Document name is required."] });
            setLoading(false);
            return;
        }

        if (!document.document_type_id) {
            setErrors({ document_type_id: ["Document type is required."] });
            setLoading(false);
            return;
        }

        const method = "POST";
        const url = isEditing ? `${API_URL}/documents/${id}/update` : `${API_URL}/documents`;

        try {
            const formData = new FormData();

            // Append all document fields - ensure document_type_id is properly formatted
            formData.append('name', document.name.trim());
            formData.append('description', document.description || '');
            formData.append('document_type_id', document.document_type_id);
            formData.append('status', document.status);

            // Append file only if it's a new file or creating new document
            if (document.file) {
                formData.append('file', document.file);
            }

            // For debugging - log what we're sending
            console.log('Sending document data:', {
                name: document.name,
                document_type_id: document.document_type_id,
                status: document.status,
                description: document.description,
                hasFile: !!document.file
            });

            const response = await fetch(url, {
                method: method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Don't set Content-Type for FormData - let browser set it with boundary
                },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                setNotification({ 
                    message: isEditing 
                        ? "Document updated successfully." 
                        : "Document created successfully.", 
                    type: "success" 
                });
                
                // Navigate to documents list after short delay
                setTimeout(() => {
                    navigate("/dashboard/documents");
                }, 1500);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                const errorData = await response.json();
                console.error('Server validation errors:', errorData);
                setErrors(errorData || {});
                setNotification({ 
                    message: errorData.message || "An error occurred while saving the document.", 
                    type: "danger" 
                });
            }
        } catch (error) {
            console.error('Network error:', error);
            setNotification({ 
                message: "An error occurred while saving the document.", 
                type: "danger" 
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseNotification = () => {
        setNotification(null);
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return "file";
        
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "file-pdf";
            case 'doc':
            case 'docx':
                return "file-word";
            default:
                return "file";
        }
    };

    const getFileTypeText = (fileName) => {
        if (!fileName) return "Unknown";
        
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "PDF Document";
            case 'doc':
                return "Word Document (DOC)";
            case 'docx':
                return "Word Document (DOCX)";
            default:
                return extension?.toUpperCase() || "File";
        }
    };

    return (
        <main className="container">
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">
                            {isEditing ? `Update Document '${document.name}'` : "Upload New Document"}
                        </p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/documents" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to Documents
                        </Link>
                        {isEditing && (
                            <>
                                &nbsp;&nbsp;
                                <Link 
                                    to={`/dashboard/documents/${id}/details`} 
                                    className="button primary-bg"
                                >
                                    <i className="fa fa-eye" aria-hidden="true"></i> &nbsp; View Details
                                </Link>
                            </>
                        )}
                    </p>
                </div>
            </nav>

            {notification && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={handleCloseNotification}
                />
            )}

            <div className="box">
                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            <div className="field">
                                <label className="label">Document Name *</label>
                                <div className="control has-icons-left">
                                    <input
                                        type="text"
                                        name="name"
                                        value={document.name}
                                        onChange={handleChange}
                                        className={`input ${errors.name ? 'is-danger' : ''}`}
                                        placeholder="Enter document name"
                                        required
                                    />
                                    <span className="icon is-small is-left">
                                        <i className="fas fa-file"></i>
                                    </span>
                                </div>
                                {errors.name && errors.name.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            <div className="field">
                                <label className="label">Document Type *</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.document_type_id ? 'is-danger' : ''}`}>
                                        <select 
                                            name="document_type_id" 
                                            value={document.document_type_id} 
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Document Type</option>
                                            {documentTypes.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.document_type_id && errors.document_type_id.map((error, index) => (
                                        <p key={index} className="help is-danger">{error}</p>
                                    ))}
                                </div>
                            </div>

                            <div className="field">
                                <label className="label">Status</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.status ? 'is-danger' : ''}`}>
                                        <select 
                                            name="status" 
                                            value={document.status} 
                                            onChange={handleChange}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    {errors.status && errors.status.map((error, index) => (
                                        <p key={index} className="help is-danger">{error}</p>
                                    ))}
                                </div>
                            </div>

                            <div className="field">
                                <label className="label">Description</label>
                                <div className="control">
                                    <textarea
                                        name="description"
                                        value={document.description}
                                        onChange={handleChange}
                                        className={`textarea ${errors.description ? 'is-danger' : ''}`}
                                        placeholder="Enter document description"
                                        rows="4"
                                    />
                                    {errors.description && errors.description.map((error, index) => (
                                        <p key={index} className="help is-danger">{error}</p>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="column is-half">
                            <div className="field">
                                <label className="label">
                                    {isEditing ? "Update Document File (Optional)" : "Document File *"}
                                </label>
                                <div className="control">
                                    <div className="file has-name is-fullwidth">
                                        <label className="file-label">
                                            <input
                                                type="file"
                                                className="file-input"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx"
                                                required={!isEditing} // Required only for new documents
                                            />
                                            <span className="file-cta">
                                                <span className="file-icon">
                                                    <i className="fas fa-upload"></i>
                                                </span>
                                                <span className="file-label">
                                                    Choose file…
                                                </span>
                                            </span>
                                            <span className="file-name">
                                                {document.file ? document.file.name : (existingFileName || "No file chosen")}
                                            </span>
                                        </label>
                                    </div>
                                    {errors.file && errors.file.map((error, index) => (
                                        <p key={index} className="help is-danger">{error}</p>
                                    ))}
                                </div>
                            </div>

                            {existingFileName && (
                                <div className="field">
                                    <label className="label">Current File</label>
                                    <div className="control">
                                        <div className="notification is-light is-info">
                                            <div className="level">
                                                <div className="level-left">
                                                    <div className="level-item">
                                                        <span className="icon">
                                                            <i className={`fas fa-${getFileIcon(existingFileName)}`}></i>
                                                        </span>
                                                        <span>
                                                            <strong>{existingFileName}</strong>
                                                            <br />
                                                            <small>{getFileTypeText(existingFileName)}</small>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="help">
                                            Leave the file field empty to keep the current file.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="content">
                                <h5>Allowed File Types:</h5>
                                <ul>
                                    <li>
                                        <span className="icon has-text-info">
                                            <i className="fas fa-file-pdf"></i>
                                        </span>
                                        PDF Documents (.pdf)
                                    </li>
                                    <li>
                                        <span className="icon has-text-primary">
                                            <i className="fas fa-file-word"></i>
                                        </span>
                                        Word Documents (.doc, .docx)
                                    </li>
                                </ul>
                                <p><strong>Maximum file size:</strong> 10MB</p>
                            </div>

                            <div className="field">
                                <div className="control mt-6">
                                    <button 
                                        className={`button primary-bg is-fullwidth ${loading ? 'is-loading' : ''}`} 
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {isEditing ? "Update Document" : "Upload Document"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </main>
    );
}

export default DocumentUpdate;